import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Upload, Download, Loader2, CheckCircle, FileSpreadsheet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const STATUS = {
  VALID: ['Valide', 'bg-green-100 text-green-800'],
  IMPORTED: ['Importé', 'bg-green-100 text-green-800'],
  ERROR: ['Erreur', 'bg-red-100 text-red-800'],
  FAILED: ['Échec', 'bg-red-100 text-red-800'],
  DUPLICATE: ['Doublon', 'bg-amber-100 text-amber-800'],
};

const fileGet = async (path, name) => {
  try {
    const res = await axios.get(`${API}${path}`, { responseType: 'blob' });
    downloadBlob(res.data, name);
  } catch (e) {
    toast.error('Téléchargement impossible');
  }
};

const Summary = ({ s }) => (
  <div className="flex flex-wrap gap-2 text-sm" data-testid="import-summary">
    <Badge className="bg-gray-100 text-gray-800">{s.total} ligne(s)</Badge>
    {s.status === 'CONFIRMED'
      ? <Badge className="bg-green-100 text-green-800" data-testid="import-imported-count">{s.imported} importée(s)</Badge>
      : <Badge className="bg-green-100 text-green-800" data-testid="import-valid-count">{s.valid} valide(s) · {Number(s.amount_valid).toLocaleString('fr-FR')} GNF</Badge>}
    <Badge className="bg-red-100 text-red-800" data-testid="import-error-count">{s.errors} erreur(s)</Badge>
    <Badge className="bg-amber-100 text-amber-800" data-testid="import-duplicate-count">{s.duplicates} doublon(s)</Badge>
  </div>
);

const RowsTable = ({ rows }) => (
  <div className="max-h-96 overflow-auto border rounded-md" data-testid="import-rows-table">
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 sticky top-0">
        <tr>{['Ligne', 'Résultat', 'Facture', 'Élève', 'Montant', 'Date', 'Référence', 'Message'].map((h) => (
          <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>))}</tr>
      </thead>
      <tbody className="divide-y">
        {rows.map((r) => (
          <tr key={r.line} data-testid={`import-row-${r.line}`}>
            <td className="px-3 py-2">{r.line}</td>
            <td className="px-3 py-2"><Badge className={STATUS[r.status]?.[1]}>{STATUS[r.status]?.[0] || r.status}</Badge></td>
            <td className="px-3 py-2">{r.invoice_number || '-'}</td>
            <td className="px-3 py-2">{r.student_name || '-'}</td>
            <td className="px-3 py-2">{r.amount != null ? Number(r.amount).toLocaleString('fr-FR') : '-'}</td>
            <td className="px-3 py-2">{r.payment_date_label}</td>
            <td className="px-3 py-2">{r.reference || '-'}</td>
            <td className="px-3 py-2 text-gray-600">{r.receipt_number ? `Reçu ${r.receipt_number}` : r.messages.join(' ; ')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const History = ({ items }) => items.length > 0 && (
  <div className="space-y-2" data-testid="import-history">
    <p className="text-sm font-medium">Imports récents</p>
    {items.map((h) => (
      <div key={h.id} className="flex items-center justify-between text-sm border rounded-md px-3 py-2">
        <span>{new Date(h.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} · {h.filename} · {h.created_by_name}</span>
        <span className="flex items-center gap-3">
          <span className="text-gray-600">{h.status === 'CONFIRMED' ? `${h.imported} importée(s), ${h.errors + h.duplicates} rejetée(s)` : 'Non confirmé'}</span>
          <Button variant="ghost" size="sm" aria-label="Télécharger le rapport" title="Télécharger le rapport" data-testid={`import-history-report-${h.id}`} onClick={() => fileGet(`/finance/payments-import/${h.id}/report`, `rapport_import_${h.id.slice(0, 8)}.xlsx`)}>
            <Download className="w-4 h-4" />
          </Button>
        </span>
      </div>
    ))}
  </div>
);

export const PaymentsImportPanel = ({ onImported }) => {
  const inputRef = useRef(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = () => axios.get(`${API}/finance/payments-import`).then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/finance/payments-import/preview`, fd);
      setResult(data);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lecture du fichier impossible');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/finance/payments-import/${result.id}/confirm`);
      setResult(data);
      toast.success(`${data.imported} paiement(s) importé(s)`);
      loadHistory();
      onImported?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import impossible');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="payments-import-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5 text-blue-700" /> Import Excel des paiements</CardTitle>
        <CardDescription>
          Chaque ligne est reliée à une facture par son N° ou par matricule élève + titre. Vous vérifiez l’aperçu, puis seules les lignes valides sont enregistrées (avec reçu).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => fileGet('/finance/payments-import/template', 'modele_import_paiements.xlsx')} data-testid="download-import-template-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Télécharger le modèle
          </Button>
          <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="import-file-input" />
          <Button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="import-choose-file-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Choisir un fichier .xlsx
          </Button>
        </div>
        {result && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Summary s={result} />
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => fileGet(`/finance/payments-import/${result.id}/report`, `rapport_import_${result.id.slice(0, 8)}.xlsx`)} data-testid="download-import-report-btn">
                  <Download className="w-4 h-4 mr-2" /> Rapport
                </Button>
                {result.status === 'PREVIEW' && (
                  <Button size="sm" onClick={confirm} disabled={busy || result.valid === 0} className="bg-green-700 hover:bg-green-800" data-testid="import-confirm-btn">
                    <CheckCircle className="w-4 h-4 mr-2" /> Importer {result.valid} ligne(s) valide(s)
                  </Button>
                )}
              </div>
            </div>
            <RowsTable rows={result.rows} />
          </div>
        )}
        <History items={history} />
      </CardContent>
    </Card>
  );
};

export default PaymentsImportPanel;
